import { navigationEpoch } from '../../navigation/loadEpoch';
import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import type { MetricConfig, MetricInstance, DateRangePreset, OrderByDirection } from '../../types';
import { createEmptyFilterGroup, createFilterCondition, deserializeFilterGroup } from '../helpers/filterHelpers';
import { saveUserSettings } from '../helpers/persistenceHelpers';
import { generateId, resolvePresetDates } from '../../utils';

/** The answer a Query Builder was opened from: where "Back to conversation" returns to. */
export interface QueryOrigin { threadId: string | null; turnId: string | null }

/** Why Explore stayed on the conversation: what it could not open. */
export const PERSPECTIVE_GONE = 'This query’s perspective is not available to you any more, so it can’t be opened.';

/** The Query Builder's presets a spec may carry (`DateRangePreset` but CUSTOM, which keeps the spec's dates). */
const SPEC_PRESETS = new Set<string>(['YESTERDAY', 'LAST_7D', 'WTD', 'MTD', 'QTD', 'YTD', 'LAST_MONTH', 'LAST_52_WEEKS', 'LAST_2_YEARS', 'LAST_3_YEARS']);

/**
 * A query spec's window as the Query Builder's date state, read as the compiler reads it (`agent/data/spec._window`):
 * a preset wins over dates (CUSTOM keeps them), resolved now; the top-level dates, else `date_range`'s only when
 * neither top-level bound is there; either end may be open. Null when the spec has no window, which the compiler runs
 * over all time and the Query Builder cannot express (it asks for a date range).
 */
export function specWindow(spec: any): { dateRangePreset: DateRangePreset; dateRangeStart: string | null; dateRangeEnd: string | null } | null {
  const preset: string | null = spec?.dateRangePreset || null;
  const nested = spec?.dateRangeStart == null && spec?.dateRangeEnd == null && spec?.date_range && typeof spec.date_range === 'object'
    && !Array.isArray(spec.date_range);
  const dateRangeStart = (nested ? spec.date_range.start : spec?.dateRangeStart) || null;
  const dateRangeEnd = (nested ? spec.date_range.end : spec?.dateRangeEnd) || null;
  if (preset && preset !== 'CUSTOM' && SPEC_PRESETS.has(preset)) {
    const { start, end } = resolvePresetDates(preset as DateRangePreset);
    return { dateRangePreset: preset as DateRangePreset, dateRangeStart: start, dateRangeEnd: end };
  }
  return dateRangeStart || dateRangeEnd ? { dateRangePreset: 'CUSTOM', dateRangeStart, dateRangeEnd } : null;
}

/** The objective page's tabs (#571). */
export type ObjectiveTab = 'plan' | 'results' | 'knowledge' | 'usage' | 'activity';
export const OBJECTIVE_TABS: readonly ObjectiveTab[] = ['plan', 'results', 'knowledge', 'usage', 'activity'];

/** The New objective page (#573) is `/objectives/new`: an objective id that is never an agent's. */
export const NEW_OBJECTIVE = 'new';

export type BrainTab = 'topics' | 'live' | 'catalog' | 'definitions' | 'graph' | 'claims' | 'sources' | 'review';

export interface NavigationSlice {
  activityTab: 'posts' | 'changes' | 'events';
  activityKinds: string[];
  navigationPending: number;
  /** The two rail destinations hosted at `/`: Home is the queue; Ask owns the durable thread strip. */
  landingSurface: 'home' | 'ask';
  /** Home is the chat (#765): the objective whose space the landing view shows, or null for the New-chat screen. */
  objectiveSpaceId: string | null;
  currentView: 'pipelines' | 'pipeline_brain' | 'activity' | 'elsewhere' | 'brain' | 'landing' | 'workspace' | 'studio' | 'browse' | 'history' | 'workflows' | 'dashboards' | 'connections' | 'metric-graph' | 'definitions' | 'reviews' | 'agents' | 'agent_wall' | 'agent_studio' | 'feed' | 'extension' | 'objectives' | 'setup' | 'builds';
  /** The agent whose wall is on screen (#202); reached from any agent identity, not the rail. */
  agentWallId: string | null;
  /** Which view of that wall is open (#472): the wall itself or what the agent knows. */
  agentWallTab: 'wall' | 'knowledge';
  /** The wall was opened as the parked surface (#571): every other way in lands on the objective. */
  agentWallParked: boolean;
  /** The objective on screen (#571) — an agent id — or null for the Objectives list. */
  objectiveId: string | null;
  /** The build on screen (#1624), a job id, or null for the Builds list. */
  buildId: string | null;
  pipelineId: string | null;
  /** The Brain (#2288): a subject on screen (`table:raw.orders`), or null for the Brain's home. */
  pipelineBrainSubject: string | null;
  objectiveTab: ObjectiveTab;
  /** A turn a `/threads/<id>?turn=` link pointed at (#472): scrolled to and highlighted in that thread. */
  threadTurnAnchor: { threadId: string; turnId: string } | null;
  /** The thread a `/threads/<id>?parked=1` link opened (#1508): its walk rail, which the simplified app parks, is shown. */
  threadRailParked: string | null;
  brainTab: BrainTab;
  brainMetric: string | null;
  /** The topic page open on Knowledge › Topics (#576), or null for the list. */
  brainTopic: string | null;
  /** The focused graph lens's centre on Knowledge › Graph (#578) — a metric id or `topic:<id>` — or null for the full graph. */
  brainLens: string | null;
  connectionWizardProvider: string | null;
  /** A New objective seed: a metric (the Brain's link) or a proposal's words (#575, `/objectives/new?text=`). */
  /** `fromThread`: the thread whose answer said "Track this as an objective" (#766); committing ties it to the objective. */
  newAgentObjective: { metric?: string; perspective?: string; text?: string; fromThread?: string | null } | null;
  activityAgentId: string | null;
  navigateToActivity: (agentId?: string) => void;
  navigateToElsewhere: () => void;
  navigateToNewAgent: (metric?: string, perspective?: string, fromThread?: string | null) => void;
  navigateToBrain: (tab?: BrainTab, metric?: string, topic?: string) => void;
  /** Knowledge › Graph's focused lens centred on a metric or `topic:<id>` (#578). */
  openLens: (center: string) => void;

  navigateToWorkspace: (domainId: string, perspectiveId: string, signal?: AbortSignal) => Promise<void>;
  /** Opens the Query Builder on `spec`; `origin` is the answer it came from. Resolves to why it could not be opened, else null. */
  navigateToWorkspaceWithSpec: (spec: any, origin?: QueryOrigin) => Promise<string | null>;
  /** Set while the Query Builder holds an answer's query. */
  queryOrigin: QueryOrigin | null;
  /** "Back to conversation": that thread, at that turn. */
  backToConversation: () => void;
  navigateToLanding: () => void;
  navigateToAsk: () => void;
  /** An objective's space in the chat sidebar (#765): its progress, links and chats, on the landing view. */
  openObjectiveSpace: (id: string) => void;
  navigateToStudio: () => void;
  navigateToStudioWithContext: () => Promise<void>;
  navigateToBrowse: () => void;
  navigateToHistory: () => void;
  navigateToWorkflows: () => void;
  navigateToMetricGraph: () => void;
  navigateToDashboards: () => void;
  navigateToConnections: () => void;
  /** The in-app extension preview (#570): Connections › Browser extension. */
  navigateToExtensionPreview: () => void;
  /** Set up a semantic layer (#1622): the wizard at `/setup`. */
  navigateToSetup: () => void;
  /** Setup with a warehouse already chosen, from Sources: the setup page takes the id once. */
  navigateToSetupWith: (connectionId: string) => void;
  setupConnectionId: string | null;
  /** Builds (#1624): the list at `/builds`, or one build at `/builds/<job id>`. */
  navigateToBuilds: (jobId?: string | null) => void;
  /** Pipelines (#2263): the list at `/pipelines`, or one pipeline at `/pipelines/<id>`. */
  navigateToPipelines: (id?: string | null) => void;
  /** The ETL builder's Brain (#2288): `/pipeline-brain`, or one subject at `/pipeline-brain?subject=<kind>:<name>`. */
  navigateToPipelineBrain: (subject?: string | null) => void;
  navigateToDefinitions: () => void;
  /** The Reviews index (#189): every living review, and "+ New review". */
  navigateToReviews: () => void;
  /** The feed (#201): agent posts as ladder cards, gated by the viewer's bar. */
  navigateToFeed: () => void;
  /** Objective agents (#200): every agent on a seat, and "+ New agent" — the Studio's Agents tab (#198). */
  navigateToAgents: () => void;
  navigateToObjectives: () => void;
  navigateToObjective: (id: string, tab?: ObjectiveTab) => void;
  /** The Ladder-side Studio (#198): connector grants + audit, and the agents. */
  navigateToAgentStudio: (tab?: import('./agentStudioSlice').StudioTab) => void;
  /** An agent's wall (#202): its scoreboard and signed ledger. */
  /** Opens the objective page unless `parked` asks for the parked wall. */
  navigateToAgentWall: (id: string, tab?: 'wall' | 'knowledge', options?: { parked?: boolean }) => void;
  openWorkspace: () => void;
}

export const createNavigationSlice: StateCreator<
  VoyagerState, [], [], NavigationSlice
> = (set, get) => ({
  activityTab: 'posts',
  activityKinds: [],
  navigationPending: 0,
  landingSurface: 'home',
  objectiveSpaceId: null,
  currentView: 'landing',
  agentWallId: null,
  agentWallTab: 'wall',
  agentWallParked: false,
  objectiveId: null,
  buildId: null,
  pipelineId: null,
  pipelineBrainSubject: null,
  objectiveTab: 'plan',
  threadTurnAnchor: null,
  queryOrigin: null,
  threadRailParked: null,
  brainTab: 'catalog',
  brainMetric: null,
  brainTopic: null,
  brainLens: null,
  connectionWizardProvider: null,
  setupConnectionId: null,
  newAgentObjective: null,
  activityAgentId: null,
  navigateToActivity: (agentId) => { set({ currentView: 'activity', activityAgentId: agentId || null, activityTab: agentId ? 'events' : 'posts' }); },
  navigateToElsewhere: () => { set({ currentView: 'elsewhere' }); },
  navigateToNewAgent: (metric, perspective, fromThread) => {
    // a new objective is the New objective page (#573): `/objectives/new`, pre-filled from a metric
    // (and, from an answer's "Track this as an objective" (#766), the thread it came from)
    set({ currentView: 'objectives', objectiveId: NEW_OBJECTIVE, objectiveTab: 'plan', newAgentObjective: { metric, perspective, ...(fromThread !== undefined ? { fromThread } : {}) } });
  },
  navigateToBrain: (tab, metric, topic) => {
    // Knowledge leads with Topics (#576) and the catalog is the Query Builder's landing (#577)
    if (tab === 'catalog') { get().navigateToBrowse(); return; }
    // Knowledge opens on its first tab, Findings (`claims`), as the mock does (#1511)
    const shown: BrainTab = tab || 'claims';
    set({ currentView: 'brain', brainTab: shown, brainMetric: metric || null, brainTopic: shown === 'topics' ? topic || null : null, brainLens: null });
  },
  openLens: (center) => {
    set({ currentView: 'brain', brainTab: 'graph', brainMetric: null, brainTopic: null, brainLens: center });
  },

  navigateToWorkspace: async (domainId: string, perspectiveId: string, signal?: AbortSignal) => {
    const generation = get().threadsGeneration;
    const loadEpoch = navigationEpoch();
    set({ navigationPending: get().navigationPending + 1 });
    let released = false;
    const release = () => {
      if (!released) { released = true; if (loadEpoch === navigationEpoch()) set({ navigationPending: Math.max(0, get().navigationPending - 1) }); }
    };
    signal?.addEventListener('abort', release, { once: true });
    try {
      set({ currentView: 'workspace' });
      // Without the domain layer (#464) the picker is the perspective list:
      // load it once, then pick — no domain step.
      const { domains: domainsOn } = await get().ensureFeatures();
      if (loadEpoch !== navigationEpoch() || signal?.aborted || generation !== get().threadsGeneration) return;
      if (!domainsOn) {
        if (get().perspectives.length === 0) {
          if (signal) await get().fetchDomains(signal);
      else await get().fetchDomains();
      if (loadEpoch !== navigationEpoch() || signal?.aborted || generation !== get().threadsGeneration) return;
        }
        // What selectDomain() used to reset on the way in: the opened perspective
        // is a fresh query, not the report that was loaded before.
        set({ loadedReportId: null, loadedReportName: null });
        if (signal) await get().selectPerspective(perspectiveId, signal);
      else await get().selectPerspective(perspectiveId);
        return;
      }
      // Ensure domains are loaded so the dropdown can display the selected value
      if (get().domains.length === 0) {
        if (signal) await get().fetchDomains(signal);
      else await get().fetchDomains();
      if (loadEpoch !== navigationEpoch() || signal?.aborted || generation !== get().threadsGeneration) return;
      }
      if (signal) await get().selectDomain(domainId, signal);
      else await get().selectDomain(domainId);
      if (loadEpoch !== navigationEpoch() || signal?.aborted || generation !== get().threadsGeneration) return;
      if (signal) await get().selectPerspective(perspectiveId, signal);
      else await get().selectPerspective(perspectiveId);
    } finally { signal?.removeEventListener('abort', release); release(); }
  },

  navigateToWorkspaceWithSpec: async (spec: any, origin?: QueryOrigin) => {
    const loadEpoch = navigationEpoch();
    const perspectiveId: string | undefined = spec?.perspective;
    if (!perspectiveId) return PERSPECTIVE_GONE;
    // a reopened thread reaches here before anything has listed the perspectives
    const { domains: domainsOn } = await get().ensureFeatures();
    if (!get().perspectivesList.length) await get().fetchLandingAgentPerspectives();
    if (loadEpoch !== navigationEpoch()) return null;
    const list = get().perspectivesList;
    const p = list.find((x) => x.id === perspectiveId);
    if ((list.length && !p) || (!p?.domain && domainsOn)) return PERSPECTIVE_GONE;
    await get().navigateToWorkspace(p?.domain || '', perspectiveId);
    if (loadEpoch !== navigationEpoch()) return null;

    const metrics: string[] = Array.isArray(spec.metrics) ? spec.metrics : [];
    const dimensions: string[] = Array.isArray(spec.dimensions) ? spec.dimensions : [];
    const selectedMetrics: MetricInstance[] = metrics.map((name) => ({
      id: generateId(),
      metricName: name,
    }));
    // per-metric options are keyed by the instance in the Query Builder and by name in a spec
    const configs: any[] = Array.isArray(spec.metricConfigs) ? [...spec.metricConfigs] : [];
    const metricConfigs: Record<string, MetricConfig> = {};
    for (const m of selectedMetrics) {
      const at = configs.findIndex((c) => c?.metricName === m.metricName);
      if (at >= 0) metricConfigs[m.id] = { ...configs.splice(at, 1)[0], instanceId: m.id };
    }

    let filterGroup = spec?.filters?.type === 'group' ? deserializeFilterGroup(spec.filters) : createEmptyFilterGroup();
    if (filterGroup.children.length === 0) {
      // the one reader of a stored condition, so BETWEEN and IN read as they do from a saved query
      const specFilters: any[] = Array.isArray(spec.filters) ? spec.filters : [];
      filterGroup = deserializeFilterGroup({ ...filterGroup, children: specFilters
        .map((f) => ({ type: 'condition', column: f?.dimension || f?.field || f?.column, operator: f?.op || f?.operator || '=', value: f?.value }))
        .filter((f) => f.column) });
    }

    // a spec with no window keeps the Query Builder's default, and with no date field the perspective's
    set({
      selectedMetrics,
      selectedDimensions: [...dimensions],
      filterGroup,
      metricConfigs,
      ...(Array.isArray(spec.orderBy) ? { orderBy: spec.orderBy.map((o: any) => ({ column: o.column, direction: String(o.direction || 'asc').toUpperCase() as OrderByDirection })) } : {}),
      ...(specWindow(spec) || {}),
      ...(spec.dateField ? { activeDateField: spec.dateField } : {}),
      querySQL: '',
      queryResults: null,
      queryOrigin: origin || null,
    });
    return null;
  },

  backToConversation: () => {
    const origin = get().queryOrigin;
    set({ queryOrigin: null });
    const threadId = origin?.threadId;
    // `loadThread` drops the anchor of the thread it leaves, so this one is set after it
    if (threadId) get().loadThread(threadId);
    set({ currentView: 'landing', landingSurface: 'ask',
      threadTurnAnchor: threadId && origin?.turnId ? { threadId, turnId: origin.turnId } : null });
  },

  navigateToLanding: () => {
    // Reset draft mode when leaving workspace
    const settings = { ...get().userSettings, modelSource: 'production' as const };
    saveUserSettings(settings);
    set({
      navigationPending: 0,
      queryOrigin: null,
      currentView: 'landing',
      landingSurface: 'home',
      objectiveSpaceId: null,
      selectedDomain: null,
      selectedPerspective: null,
      perspectives: [],
      semanticModel: null,
      treeNodes: [],
      selectedMetrics: [],
      selectedDimensions: [],
      metricConfigs: {},
      calculatedAttributes: [],
      filters: [],
      filterGroup: createEmptyFilterGroup(),
      defaultFilterIds: new Set<string>(),
      querySQL: '',
      queryResults: null,
      loadedReportId: null,
      loadedReportName: null,
      pinnedResults: [],
      activeResultTab: null,
      userSettings: settings,
      activeDraftId: null,
      activeDraftName: null,
      availableDrafts: [],
    });
  },

  navigateToAsk: () => {
    const selected = get().savedThreads.find((thread) => thread.id === get().currentThreadId);
    set({
      currentView: 'landing',
      landingSurface: 'ask',
      ...(selected?.kind === 'doc' ? { currentThreadId: null, threadTurnAnchor: null } : {}),
    });
  },

  openObjectiveSpace: (id) => {
    // the space sits where a new chat would: the conversation on screen keeps running in the background
    get().clearLandingChat();
    set({ currentView: 'landing', landingSurface: 'home', objectiveSpaceId: id, threadTurnAnchor: null });
    get().fetchAgents({ ifNeeded: true });
  },

  navigateToStudio: () => {
    set({ currentView: 'studio' });
  },

  navigateToStudioWithContext: async () => {
    const loadEpoch = navigationEpoch();
    const { selectedDomain, selectedPerspective, semanticModel } = get();
    if (!selectedPerspective || !semanticModel) return;

    // Import studio store dynamically to avoid circular deps
    const useStudioStore = (await import('../useStudioStore')).default;
    if (loadEpoch !== navigationEpoch()) return;
    const modelName = semanticModel.model || selectedPerspective;
    await useStudioStore.getState().loadDraftFromModel(modelName, {
      domain: selectedDomain || '',
      perspective: selectedPerspective,
    });
    if (loadEpoch !== navigationEpoch()) return;
    set({ currentView: 'studio' });
  },

  navigateToBrowse: () => {
    // the catalog is the Query Builder's landing: the perspective picker, nothing open (#577)
    get().navigateToLanding();
    get().openWorkspace();
  },

  navigateToHistory: () => {
    set({ currentView: 'history' });
  },

  navigateToWorkflows: () => {
    set({ currentView: 'workflows' });
  },

  navigateToMetricGraph: () => {
    get().navigateToBrain('graph');
  },

  navigateToDashboards: () => {
    set({ currentView: 'dashboards' });
    get().closeDashboard();
    get().fetchDashboards();
  },

  navigateToConnections: () => {
    set({ currentView: 'connections' });
    get().selectConnection(null);
    get().fetchProviders();
    get().fetchConnections();
  },

  navigateToExtensionPreview: () => {
    set({ currentView: 'extension' });
  },

  navigateToSetup: () => {
    set({ currentView: 'setup', setupConnectionId: null });
  },

  navigateToSetupWith: (connectionId) => {
    set({ currentView: 'setup', setupConnectionId: connectionId });
  },

  navigateToPipelines: (id = null) => {
    set({ currentView: 'pipelines', pipelineId: id });
  },

  navigateToPipelineBrain: (subject = null) => {
    set({ currentView: 'pipeline_brain', pipelineBrainSubject: subject });
  },

  navigateToBuilds: (jobId = null) => {
    set({ currentView: 'builds', buildId: jobId });
  },

  navigateToDefinitions: () => {
    get().navigateToBrain('definitions');
  },

  navigateToReviews: () => {
    set({ currentView: 'reviews' });
  },

  navigateToFeed: () => {
    get().navigateToActivity();
  },

  navigateToAgents: () => {
    get().navigateToObjectives();
  },

  navigateToObjectives: () => {
    set({ currentView: 'objectives', objectiveId: null });
    get().fetchAgents();
  },

  navigateToObjective: (id, tab = 'plan') => {
    set({ currentView: 'objectives', objectiveId: id, objectiveTab: tab, agentWallId: id });
    get().fetchAgents({ ifNeeded: true });
    get().fetchAgentWall(id);
  },

  navigateToAgentStudio: (tab) => {
    if (tab === 'agents') { get().navigateToAgents(); return; }
    set({ currentView: 'agent_studio', ...(tab ? { studioTab: tab } : {}) });
  },

  navigateToAgentWall: (id: string, tab = 'wall', options) => {
    if (!options?.parked) { get().navigateToObjective(id, tab === 'knowledge' ? 'knowledge' : 'plan'); return; }
    set({ currentView: 'agent_wall', agentWallId: id, agentWallTab: tab, agentWallParked: !!options?.parked });
    get().fetchAgentWall(id);
  },

  openWorkspace: () => {
    set({ currentView: 'workspace', queryOrigin: null });
  },
});
